import { useEffect, useState } from 'react';
import { api, BladApi, type ListaMontazy, type Sesja, type StatusMontazu } from '../api';
import { data } from '../format';
import { idz } from '../router';
import { IkonaPlus } from '../components/Icons';
import { NaglowekWidoku } from '../components/Shell';
import {
  ChipMontazu,
  ChipFiltra,
  Karta,
  Komunikat,
  Przycisk,
  PrzyciskMarki,
  Pusto,
  nazwaStatusuMontazu,
} from '../components/ui';

const NA_STRONE = 30;
const STATUSY: StatusMontazu[] = ['NOWE', 'W_TOKU', 'WYKONANE'];

/**
 * Lista zleceń montażu. To tu powstaje baza urządzeń — Subiekt nie przechowuje
 * numerów fabrycznych, więc bez montaży karta maszyny nie ma skąd się wziąć
 * (docs/model-danych.md §6).
 */
export function AssemblyView({ sesja, szukane = '' }: { sesja: Sesja; szukane?: string }) {
  const mozeWystawiac = sesja.permissions.includes('assembly.write');

  const [status, setStatus] = useState<StatusMontazu | null>(null);
  const [strona, setStrona] = useState(0);
  const [wynik, setWynik] = useState<ListaMontazy | null>(null);
  const [dosypane, setDosypane] = useState<ListaMontazy['montaze']>([]);
  const [blad, setBlad] = useState<string | null>(null);

  useEffect(() => {
    setStrona(0);
    setDosypane([]);
  }, [szukane, status]);

  useEffect(() => {
    let aktualne = true;
    setBlad(null);

    api
      .montaze({
        q: szukane,
        status: status ?? undefined,
        limit: NA_STRONE,
        offset: strona * NA_STRONE,
      })
      .then((odpowiedz) => {
        if (!aktualne) return;
        setWynik(odpowiedz);
        setDosypane((poprzednie) =>
          strona === 0 ? odpowiedz.montaze : [...poprzednie, ...odpowiedz.montaze],
        );
      })
      .catch((error: unknown) => {
        if (aktualne) {
          setBlad(error instanceof BladApi ? error.message : 'Nie udało się wczytać listy.');
        }
      });

    return () => {
      aktualne = false;
    };
  }, [szukane, status, strona]);

  const podsumowanie = wynik?.podsumowanie;

  return (
    <>
      <NaglowekWidoku
        tytul="Montaże"
        podtytul={
          szukane
            ? `Szukanie: ${szukane}`
            : podsumowanie
              ? `${podsumowanie.wszystkie} zleceń · tu powstaje baza urządzeń`
              : undefined
        }
        akcje={
          mozeWystawiac ? (
            <PrzyciskMarki
              type="button"
              onClick={() => idz('/montaze/nowe')}
              ikona={<IkonaPlus rozmiar={16} />}
            >
              Nowe zlecenie
            </PrzyciskMarki>
          ) : undefined
        }
      />

      {blad && (
        <div className="mb-14">
          <Komunikat tresc={blad} />
        </div>
      )}

      <div className="mb-14 flex flex-wrap items-center gap-7">
        <ChipFiltra
          type="button"
          aktywny={status === null}
          licznik={podsumowanie?.wszystkie}
          onClick={() => setStatus(null)}
        >
          Wszystkie
        </ChipFiltra>
        {STATUSY.map((kod) => (
          <ChipFiltra
            key={kod}
            type="button"
            aktywny={status === kod}
            licznik={podsumowanie?.wgStatusu[kod] ?? 0}
            onClick={() => setStatus(kod)}
          >
            {nazwaStatusuMontazu(kod)}
          </ChipFiltra>
        ))}
        <span className="t-small ml-auto text-n-500">
          Status wynika z trzech podpisów — nie da się go ustawić z listy.
        </span>
      </div>

      <Karta wypelnienie={false}>
        {wynik === null && <Pusto tresc="Wczytywanie…" />}

        {wynik !== null && dosypane.length === 0 && (
          <Pusto
            tresc={
              szukane || status
                ? 'Nic nie pasuje do tych warunków.'
                : 'Nie ma jeszcze żadnego zlecenia montażu. Pierwsza karta urządzenia powstanie właśnie stąd.'
            }
          />
        )}

        {dosypane.length > 0 && (
          <div className="overflow-x-auto">
            <table className="tab">
              <thead>
                <tr>
                  <th>Numer</th>
                  <th>Klient</th>
                  <th>Maszyna</th>
                  <th>Rodzaj</th>
                  <th>Realizacja</th>
                  <th>Podpisy</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {dosypane.map((montaz) => (
                  <tr
                    key={montaz.id}
                    data-klikalny=""
                    tabIndex={0}
                    role="link"
                    onClick={() => idz(`/montaze/${montaz.id}`)}
                    onKeyDown={(zdarzenie) => {
                      if (zdarzenie.key === 'Enter' || zdarzenie.key === ' ') {
                        zdarzenie.preventDefault();
                        idz(`/montaze/${montaz.id}`);
                      }
                    }}
                  >
                    <td>
                      <div className="t-mono-data whitespace-nowrap text-n-900">{montaz.numer}</div>
                      {montaz.numerZamowienia && (
                        <div className="t-small text-n-600">zam. {montaz.numerZamowienia}</div>
                      )}
                    </td>
                    <td className="t-body-strong text-n-900">{montaz.customer.nazwa}</td>
                    <td>
                      {/* Producent i głowica w jednej komórce — tak samo jak „Maszyna"
                          na liście urządzeń, żeby te dwie listy czytało się tak samo. */}
                      <div className="t-body-strong text-n-900">{montaz.producent ?? '—'}</div>
                      {montaz.modelGlowicy && (
                        <div className="t-small text-n-600">{montaz.modelGlowicy}</div>
                      )}
                    </td>
                    <td className="t-small text-n-600">
                      {montaz.rodzaj === 'NOWA' ? 'nowa' : 'używana'}
                    </td>
                    <td className="t-mono-data whitespace-nowrap text-n-600">
                      {data(montaz.dataRealizacji) || '—'}
                    </td>
                    <td className="t-mono-data text-n-600">{montaz.podpisow} / 3</td>
                    <td>
                      <ChipMontazu status={montaz.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {wynik !== null && dosypane.length < wynik.ile && (
          <div className="border-t border-n-100 px-14 py-10">
            <Przycisk type="button" onClick={() => setStrona((s) => s + 1)}>
              Pokaż kolejne ({wynik.ile - dosypane.length})
            </Przycisk>
          </div>
        )}
      </Karta>
    </>
  );
}
